import React from 'react';
import { Award, Heart, Stethoscope, Target } from 'lucide-react';

const About = () => {
  return (
    <div className="about-page">
      {/* Hero Section */}
      <section className="bg-cream py-16">
        <div className="container mx-auto px-4">
          <div className="max-w-5xl mx-auto">
            <h1 className="text-5xl font-bold text-dark-text mb-6 text-center" data-testid="about-heading">
              Dr. Sanjay Panicker
            </h1>
            <p className="text-2xl text-golden text-center mb-12">Homeopathic Physician</p>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
              {/* Doctor Image */}
              <div className="flex justify-center">
                <img 
                  src="/images/doctor.jpeg" 
                  alt="Dr. Sanjay Panicker" 
                  className="rounded-lg shadow-2xl w-full max-w-md"
                  data-testid="doctor-image"
                />
              </div>

              {/* Doctor Info */}
              <div>
                <p className="text-lg text-gray-700 leading-relaxed mb-6" data-testid="doctor-intro">
                  Dr. Sanjay Panicker is the founder of <strong>Amrita Homeopathy</strong>, with extensive clinical experience in treating patients using <strong>individualized, root-cause-based homeopathic care</strong>.
                </p>
                <p className="text-lg text-gray-700 leading-relaxed">
                  His approach emphasizes understanding the patient, not just symptoms, supporting the body's natural healing response through ethical, evidence-aware practice.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Philosophy Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <h2 className="text-4xl font-bold text-center text-dark-text mb-12" data-testid="philosophy-heading">
            Our Clinical Philosophy
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            <div className="text-center" data-testid="philosophy-patient">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-dark-text">Patient-Centered Care</h3>
              <p className="text-gray-700 leading-relaxed">
                Understanding the patient, not just symptoms. Every individual receives personalized attention.
              </p>
            </div>

            <div className="text-center" data-testid="philosophy-natural">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Stethoscope className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-dark-text">Natural Healing</h3>
              <p className="text-gray-700 leading-relaxed">
                Supporting the body's natural healing response through gentle, time-tested homeopathic principles.
              </p>
            </div>

            <div className="text-center" data-testid="philosophy-ethical">
              <div className="bg-golden w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4">
                <Award className="text-white" size={32} />
              </div>
              <h3 className="text-xl font-semibold mb-3 text-dark-text">Ethical Practice</h3>
              <p className="text-gray-700 leading-relaxed">
                Evidence-aware, responsible practice that prioritizes patient safety and realistic expectations.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Amrita Sparsha Story */}
      <section className="py-16 bg-cream">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-4xl font-bold text-center text-dark-text mb-8" data-testid="sparsha-story-heading">
              The Amrita Sparsha Story
            </h2>
            <div className="bg-white p-8 rounded-lg shadow-lg">
              <p className="text-lg text-gray-700 leading-relaxed mb-6" data-testid="sparsha-story-text">
                <strong>Amrita Sparsha</strong> was developed under Dr. Panicker's guidance to provide <strong>supportive wellness products</strong> that complement clinical treatment—never replacing professional consultation.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed mb-6">
                Each product is carefully formulated with the same principles that guide our clinical practice: safety, efficacy, and respect for the body's natural healing processes.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                Our products are designed to support skin, hair, and personal wellness as part of a balanced care routine. They are not intended to diagnose or cure medical conditions, but rather to complement your overall wellness journey.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 golden-gradient">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6" data-testid="about-cta-heading">
            Experience Doctor-Guided Wellness
          </h2>
          <p className="text-white text-lg mb-8 max-w-2xl mx-auto">
            Book a consultation with Dr. Sanjay Panicker or explore our wellness products.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <a 
              href="/contact" 
              className="bg-white text-golden px-8 py-4 rounded-lg font-semibold text-lg hover:bg-cream transition-colors"
              data-testid="about-cta-consult-button"
            >
              Book Consultation
            </a>
            <a 
              href="/products" 
              className="border-2 border-white text-white px-8 py-4 rounded-lg font-semibold text-lg hover:bg-white hover:text-golden transition-colors"
              data-testid="about-cta-products-button"
            >
              View Products
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;